import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import BioEditor, { overLimit } from "@/components/bios/BioEditor";
import api, { formatApiError } from "@/lib/api";

// Director edits any team member's bio + their role for the current list (subgroup or whole team).
export default function BioEditDialog({ member, program, subgroupId, groupName, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(member?.bio || ""); setRole(member?.role || ""); }, [member]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/bios/member/${member.member_id}`, { bio: text, role, ...(subgroupId && { subgroup_id: subgroupId }) });
      toast.success(`${member.first_name || member.name}'s bio saved`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="bio-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit bio · {member?.name}</DialogTitle>
          <DialogDescription>Changes appear on the member's Profile and in your exported PDF. The member is notified when their bio changes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Role / position {groupName ? <span className="font-normal text-slate-500">in {groupName}</span> : <span className="font-normal text-slate-500">(whole team)</span>}</Label>
          <Input value={role} maxLength={80} onChange={(e) => setRole(e.target.value)} data-testid="bio-edit-role"
            placeholder={program === "theatre" ? "e.g. Emily Webb, Stage Manager, Ensemble" : "e.g. Captain, Lieutenant, Officer"} />
          <p className="text-xs text-slate-400">Shown as "Name (Role)" on this list, in the PDF and in copied bios.</p>
        </div>
        <BioEditor value={text} onChange={setText} program={program} testId="bio-edit-textarea" />
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="bio-edit-cancel">Cancel</Button>
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={saving || overLimit(text)} onClick={save} data-testid="bio-edit-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
